import { createApi } from '@reduxjs/toolkit/query/react';

import { apiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import type {
  BrokerActiveDealRow,
  BrokerDashboardAlerts,
  BrokerDashboardStats,
  BrokerDealDetail,
  BrokerDealRow,
  BrokerDocumentsSummary,
  BrokerDocumentType,
  BrokerEnquiryBreakdown,
  BrokerEnquiryDetail,
  BrokerEnquiryRow,
  BrokerMarketSnapshot,
  BrokerNotification,
  BrokerProfileResult,
  BrokerPropertyDetail,
  BrokerPropertyQueryArgs,
  BrokerPropertyRow,
  BrokerSavedPropertyRow,
  BrokerSavedQuickRow,
  CreateBrokerEnquiryArgs,
  DealNoteRow,
  IdResult,
  MessageResult,
  Paged,
  UpdateBrokerProfileArgs,
  UploadDealDocumentResult
} from './brokerTypes';

/**
 * `/api/broker/*` — the marketplace as a broker sees it: dashboard aggregates, the masked
 * listing browse, bookmarks, enquiries, deals, profile and notifications.
 *
 * Ported endpoint-for-endpoint from the axios `services/brokerService.ts`. Every url, method,
 * query parameter and body is unchanged, because the backend contract is frozen.
 *
 * The normalizers below are exported ONLY so the colocated test can reach them. They are
 * module-scope, take `unknown`, and are TOTAL: they never throw and never return `undefined`.
 * A throwing normalizer takes out the route's error boundary instead of showing the query's
 * own error state, which is a far worse failure than a zeroed card.
 */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asRecord = (value: unknown): Record<string, unknown> => (isRecord(value) ? value : {});

/** Row arrays are structurally checked and then typed — a non-object entry is dropped, not kept. */
const asRows = <T>(value: unknown): T[] =>
  (Array.isArray(value) ? value.filter(isRecord) : []) as unknown as T[];

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asOptionalString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const asNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const asOptionalNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const asBoolean = (value: unknown): boolean => value === true;

/** The one envelope: `{ success, message?, data }`. A reply with no `data` yields `{}`. */
const envelope = (response: unknown): unknown => asRecord(response).data;

/**
 * Both list envelopes in one shape. `paginated()` answers `{ items, pagination }`; the admin
 * lists answer `{ items, total, page, limit }` with no `totalPages` at all. `totalPages` is
 * DERIVED when absent (`Math.ceil(total / limit) || 1`, exactly what the backend does) — that
 * is arithmetic on values the server sent, not an invented identifier.
 */
export function normalizePaged<T>(response: unknown): Paged<T> {
  const data = asRecord(envelope(response));
  const pagination = asRecord(data['pagination']);
  const items = asRows<T>(data['items']);
  const page = asNumber(pagination['page'] ?? data['page'], 1);
  const limit = asNumber(pagination['limit'] ?? data['limit'], 0);
  const total = asNumber(pagination['total'] ?? data['total'], items.length);
  const derived = limit > 0 ? Math.ceil(total / limit) || 1 : 1;
  return {
    items,
    page,
    limit,
    total,
    totalPages: asNumber(pagination['totalPages'] ?? data['totalPages'], derived)
  };
}

/** A bare `data: [...]` reply. An object or a null `data` yields `[]`, never a crash. */
export function normalizeRows<T>(response: unknown): T[] {
  return asRows<T>(envelope(response));
}

/** `okMessage(res, …)` replies carry `message` at the ROOT — there is no `data` to unwrap. */
export function normalizeMessage(response: unknown): MessageResult {
  return { message: asString(asRecord(response)['message']) };
}

/** `ok(res, { id }, message)`. A payload with no usable id yields `null`; nothing is invented. */
export function normalizeIdResult(response: unknown): IdResult {
  const root = asRecord(response);
  return {
    id: asOptionalNumber(asRecord(root['data'])['id']),
    message: asString(root['message'])
  };
}

export function normalizeBrokerStats(response: unknown): BrokerDashboardStats {
  const data = asRecord(envelope(response));
  return {
    totalEnquiriesSent: asNumber(data['totalEnquiriesSent']),
    enquiriesApprovedByAdmin: asNumber(data['enquiriesApprovedByAdmin']),
    activeDeals: asNumber(data['activeDeals']),
    dealsClosed: asNumber(data['dealsClosed']),
    propertiesBookmarked: asNumber(data['propertiesBookmarked']),
    pendingEnquiries: asNumber(data['pendingEnquiries']),
    moreInfoNeeded: asNumber(data['moreInfoNeeded']),
    successRate: asNumber(data['successRate']),
    totalDeals: asNumber(data['totalDeals']),
    dealsLost: asNumber(data['dealsLost'])
  };
}

export function normalizeBrokerAlerts(response: unknown): BrokerDashboardAlerts {
  const data = asRecord(envelope(response));
  return {
    moreInfoNeeded: asNumber(data['moreInfoNeeded']),
    newlyApproved: asNumber(data['newlyApproved'])
  };
}

export function normalizeEnquiryBreakdown(response: unknown): BrokerEnquiryBreakdown {
  const data = asRecord(envelope(response));
  return {
    pending: asNumber(data['pending']),
    underReview: asNumber(data['underReview']),
    approved: asNumber(data['approved']),
    rejected: asNumber(data['rejected']),
    moreInfoNeeded: asNumber(data['moreInfoNeeded']),
    approvalRate: asNumber(data['approvalRate'])
  };
}

export function normalizeMarketSnapshot(response: unknown): BrokerMarketSnapshot {
  const data = asRecord(envelope(response));
  const byLandType: Record<string, number> = {};
  for (const [key, value] of Object.entries(asRecord(data['byLandType']))) {
    byLandType[key] = asNumber(value);
  }
  const byDistrict = asRows<Record<string, unknown>>(data['byDistrict']).map((row) => ({
    district: asString(row['district']),
    count: asNumber(row['count'])
  }));
  return {
    totalVerified: asNumber(data['totalVerified']),
    newThisWeek: asNumber(data['newThisWeek']),
    byLandType,
    byDistrict
  };
}

const asDocumentsSummary = (value: unknown): BrokerDocumentsSummary => {
  const raw = asRecord(value);
  const onFile = (type: BrokerDocumentType): boolean => asBoolean(raw[type]);
  return {
    patta: onFile('patta'),
    chitta: onFile('chitta'),
    parent_doc: onFile('parent_doc'),
    rsr: onFile('rsr'),
    adangal: onFile('adangal'),
    topo: onFile('topo'),
    original_doc: onFile('original_doc'),
    ec: onFile('ec'),
    sale_deed: onFile('sale_deed')
  };
};

/** The masked detail card. Nested summaries are rebuilt so a missing branch cannot throw. */
export function normalizeBrokerProperty(response: unknown): BrokerPropertyDetail | null {
  const data = asRecord(envelope(response));
  const id = asOptionalNumber(data['id']);
  if (id === null) return null;

  const ownership = asRecord(data['ownership_summary']);
  const opinion = asRecord(data['legal_opinion_summary']);
  const cleanTitle = asString(ownership['clean_title'], 'no');

  return {
    id,
    title: asString(data['title']),
    land_type: asOptionalString(data['land_type']),
    survey_number_masked: asOptionalString(data['survey_number_masked']),
    area: (data['area'] as string | number | null) ?? null,
    area_unit: asOptionalString(data['area_unit']),
    district: asOptionalString(data['district']),
    state: asOptionalString(data['state']),
    asking_price: (data['asking_price'] as string | number | null) ?? null,
    price_unit: asOptionalString(data['price_unit']),
    is_negotiable: asOptionalNumber(data['is_negotiable']),
    urgency: asOptionalString(data['urgency']),
    legal_verified: asBoolean(data['legal_verified']),
    risk_score: asOptionalNumber(data['risk_score']),
    risk_level: asString(data['risk_level'], 'low') as BrokerPropertyDetail['risk_level'],
    documents_summary: asDocumentsSummary(data['documents_summary']),
    ownership_summary: {
      previous_owners_count: asNumber(ownership['previous_owners_count']),
      ownership_history_years: asNumber(ownership['ownership_history_years']),
      clean_title:
        cleanTitle === 'yes' || cleanTitle === 'conditional' || cleanTitle === 'no'
          ? cleanTitle
          : 'no'
    },
    legal_opinion_summary: {
      decision: asOptionalString(opinion['decision']),
      remarks: asOptionalString(opinion['remarks'])
    },
    is_saved: asBoolean(data['is_saved']),
    photos: asRows(data['photos']),
    videos: asRows(data['videos']),
    youtubeLinks: asRows(data['youtubeLinks'])
  };
}

/** `{ deal, property, stage_history, documents }`. Every branch is independently optional. */
/**
 * `SELECT e.*` passthrough. No numeric `id` means the enquiry is not there — `null`, not a
 * husk of an object the detail drawer would render as blank fields.
 */
export function normalizeBrokerEnquiry(response: unknown): BrokerEnquiryDetail | null {
  const data = asRecord(envelope(response));
  return asOptionalNumber(data['id']) === null ? null : (data as unknown as BrokerEnquiryDetail);
}

export function normalizeBrokerDeal(response: unknown): BrokerDealDetail {
  const data = asRecord(envelope(response));
  const deal = asRecord(data['deal']);
  const property = asRecord(data['property']);
  return {
    deal:
      asOptionalNumber(deal['id']) === null ? null : (deal as unknown as BrokerDealDetail['deal']),
    property:
      asOptionalNumber(property['id']) === null
        ? null
        : (property as unknown as BrokerDealDetail['property']),
    stage_history: asRows(data['stage_history']),
    documents: asRows(data['documents'])
  };
}

export function normalizeBrokerProfile(response: unknown): BrokerProfileResult {
  const data = asRecord(envelope(response));
  const user = asRecord(data['user']);
  const stats = asRecord(data['stats']);
  return {
    /** A broker with no profile row gets `{}` from the backend, never `null`. Preserved. */
    user:
      asOptionalNumber(user['id']) === null
        ? null
        : (user as unknown as BrokerProfileResult['user']),
    profile: asRecord(data['profile']) as BrokerProfileResult['profile'],
    stats: {
      totalEnquiriesSent: asNumber(stats['totalEnquiriesSent']),
      approvedEnquiries: asNumber(stats['approvedEnquiries']),
      dealsClosed: asNumber(stats['dealsClosed']),
      successRate: asNumber(stats['successRate'])
    }
  };
}

export function normalizeUploadedDealDocument(response: unknown): UploadDealDocumentResult {
  const data = asRecord(envelope(response));
  return { id: asOptionalNumber(data['id']), file_url: asString(data['file_url']) };
}

export const brokerApi = createApi({
  reducerPath: 'brokerApi',
  baseQuery: makeBaseQuery(apiBaseUrl),
  tagTypes: [
    'Property',
    'SavedProperty',
    'Enquiry',
    'Deal',
    'DealNote',
    'Notification',
    'Profile',
    'DashboardStats'
  ],
  endpoints: (builder) => ({
    /* ───────────── dashboard ───────────── */

    getBrokerDashboardStats: builder.query<BrokerDashboardStats, void>({
      query: () => ({ url: 'broker/dashboard/stats' }),
      transformResponse: (response: unknown) => normalizeBrokerStats(response),
      providesTags: [{ type: 'DashboardStats', id: 'BROKER' }]
    }),

    /**
     * `/dashboard/activity` is served by the notifications module, not the broker module —
     * it is the ten most recent notifications, read or unread.
     */
    getBrokerDashboardActivity: builder.query<BrokerNotification[], void>({
      query: () => ({ url: 'broker/dashboard/activity' }),
      transformResponse: (response: unknown) => normalizeRows<BrokerNotification>(response),
      providesTags: [{ type: 'Notification', id: 'ACTIVITY' }]
    }),

    getBrokerDashboardAlerts: builder.query<BrokerDashboardAlerts, void>({
      query: () => ({ url: 'broker/dashboard/alerts' }),
      transformResponse: (response: unknown) => normalizeBrokerAlerts(response),
      providesTags: [{ type: 'DashboardStats', id: 'ALERTS' }]
    }),

    getBrokerEnquiryBreakdown: builder.query<BrokerEnquiryBreakdown, void>({
      query: () => ({ url: 'broker/dashboard/enquiry-breakdown' }),
      transformResponse: (response: unknown) => normalizeEnquiryBreakdown(response),
      providesTags: [{ type: 'DashboardStats', id: 'ENQUIRY_BREAKDOWN' }]
    }),

    getBrokerDashboardActiveDeals: builder.query<BrokerActiveDealRow[], void>({
      query: () => ({ url: 'broker/dashboard/active-deals' }),
      transformResponse: (response: unknown) => normalizeRows<BrokerActiveDealRow>(response),
      providesTags: [{ type: 'Deal', id: 'DASHBOARD' }]
    }),

    getBrokerDashboardSavedQuick: builder.query<BrokerSavedQuickRow[], void>({
      query: () => ({ url: 'broker/dashboard/saved-quick' }),
      transformResponse: (response: unknown) => normalizeRows<BrokerSavedQuickRow>(response),
      providesTags: [{ type: 'SavedProperty', id: 'QUICK' }]
    }),

    /**
     * Two routers register `/broker/dashboard/notifications`; the broker module is mounted
     * first, so this is its four newest UNREAD rows. Do not "fix" the duplicate here.
     */
    getBrokerDashboardNotifications: builder.query<BrokerNotification[], void>({
      query: () => ({ url: 'broker/dashboard/notifications' }),
      transformResponse: (response: unknown) => normalizeRows<BrokerNotification>(response),
      providesTags: [{ type: 'Notification', id: 'MINI' }]
    }),

    getBrokerMarketSnapshot: builder.query<BrokerMarketSnapshot, void>({
      query: () => ({ url: 'broker/market-snapshot' }),
      transformResponse: (response: unknown) => normalizeMarketSnapshot(response),
      providesTags: [{ type: 'DashboardStats', id: 'MARKET' }]
    }),

    /* ───────────── properties ───────────── */

    getBrokerProperties: builder.query<Paged<BrokerPropertyRow>, BrokerPropertyQueryArgs>({
      query: (params) => ({ url: 'broker/properties', params: { ...params } }),
      transformResponse: (response: unknown) => normalizePaged<BrokerPropertyRow>(response),
      providesTags: (result) => [
        { type: 'Property' as const, id: 'LIST' },
        ...(result?.items ?? []).map((row) => ({ type: 'Property' as const, id: String(row.id) }))
      ]
    }),

    getBrokerProperty: builder.query<BrokerPropertyDetail | null, { id: number }>({
      query: ({ id }) => ({ url: `broker/properties/${id}` }),
      transformResponse: (response: unknown) => normalizeBrokerProperty(response),
      providesTags: (_result, _error, arg) => [{ type: 'Property', id: String(arg.id) }]
    }),

    /* ───────────── saved ───────────── */

    /** Idempotent by contract — a double-tap answers 200, not a duplicate-key error. */
    saveBrokerProperty: builder.mutation<IdResult, { propertyId: number }>({
      query: ({ propertyId }) => ({ url: `broker/saved/${propertyId}`, method: 'POST' }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Property', id: 'LIST' },
        { type: 'Property', id: String(arg.propertyId) },
        { type: 'SavedProperty', id: 'LIST' },
        { type: 'SavedProperty', id: 'QUICK' },
        { type: 'DashboardStats', id: 'BROKER' }
      ]
    }),

    /** Also idempotent: unsaving something that was never saved is a success. */
    unsaveBrokerProperty: builder.mutation<MessageResult, { propertyId: number }>({
      query: ({ propertyId }) => ({ url: `broker/saved/${propertyId}`, method: 'DELETE' }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Property', id: 'LIST' },
        { type: 'Property', id: String(arg.propertyId) },
        { type: 'SavedProperty', id: 'LIST' },
        { type: 'SavedProperty', id: 'QUICK' },
        { type: 'DashboardStats', id: 'BROKER' }
      ]
    }),

    getSavedProperties: builder.query<
      Paged<BrokerSavedPropertyRow>,
      { page?: number; limit?: number } | void
    >({
      query: (args) => ({ url: 'broker/saved', params: { ...(args ?? {}) } }),
      transformResponse: (response: unknown) => normalizePaged<BrokerSavedPropertyRow>(response),
      providesTags: (result) => [
        { type: 'SavedProperty' as const, id: 'LIST' },
        ...(result?.items ?? []).map((row) => ({
          type: 'SavedProperty' as const,
          id: String(row.id)
        }))
      ]
    }),

    /* ───────────── enquiries ───────────── */

    getBrokerEnquiries: builder.query<
      Paged<BrokerEnquiryRow>,
      { page?: number; limit?: number } | void
    >({
      query: (args) => ({ url: 'broker/enquiries', params: { ...(args ?? {}) } }),
      transformResponse: (response: unknown) => normalizePaged<BrokerEnquiryRow>(response),
      providesTags: (result) => [
        { type: 'Enquiry' as const, id: 'LIST' },
        ...(result?.items ?? []).map((row) => ({ type: 'Enquiry' as const, id: String(row.id) }))
      ]
    }),

    getBrokerEnquiry: builder.query<BrokerEnquiryDetail | null, { id: number }>({
      query: ({ id }) => ({ url: `broker/enquiries/${id}` }),
      transformResponse: (response: unknown) => normalizeBrokerEnquiry(response),
      providesTags: (_result, _error, arg) => [{ type: 'Enquiry', id: String(arg.id) }]
    }),

    /** Answers 200, not 201 — the buyer equivalent answers 201 and both clients depend on it. */
    createBrokerEnquiry: builder.mutation<IdResult, CreateBrokerEnquiryArgs>({
      query: (body) => ({ url: 'broker/enquiries', method: 'POST', body }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: [
        { type: 'Enquiry', id: 'LIST' },
        { type: 'DashboardStats', id: 'BROKER' },
        { type: 'DashboardStats', id: 'ALERTS' },
        { type: 'DashboardStats', id: 'ENQUIRY_BREAKDOWN' }
      ]
    }),

    deleteBrokerEnquiry: builder.mutation<MessageResult, { id: number }>({
      query: ({ id }) => ({ url: `broker/enquiries/${id}`, method: 'DELETE' }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Enquiry', id: 'LIST' },
        { type: 'Enquiry', id: String(arg.id) },
        { type: 'DashboardStats', id: 'BROKER' },
        { type: 'DashboardStats', id: 'ALERTS' },
        { type: 'DashboardStats', id: 'ENQUIRY_BREAKDOWN' }
      ]
    }),

    respondBrokerEnquiry: builder.mutation<MessageResult, { id: number; message: string }>({
      query: ({ id, message }) => ({
        url: `broker/enquiries/${id}/respond`,
        method: 'POST',
        body: { message }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Enquiry', id: 'LIST' },
        { type: 'Enquiry', id: String(arg.id) },
        { type: 'DashboardStats', id: 'ALERTS' },
        { type: 'DashboardStats', id: 'ENQUIRY_BREAKDOWN' }
      ]
    }),

    /* ───────────── deals ───────────── */

    /**
     * One endpoint, two callers: the paged table and the kanban board, which asks for
     * `{ page: 1, limit: 200 }`. The old service had a second function for that; it is the
     * same url with different args, so it is the same endpoint here.
     */
    getBrokerDeals: builder.query<Paged<BrokerDealRow>, { page?: number; limit?: number } | void>({
      query: (args) => ({ url: 'broker/deals', params: { ...(args ?? {}) } }),
      transformResponse: (response: unknown) => normalizePaged<BrokerDealRow>(response),
      providesTags: (result) => [
        { type: 'Deal' as const, id: 'LIST' },
        ...(result?.items ?? []).map((row) => ({ type: 'Deal' as const, id: String(row.id) }))
      ]
    }),

    /** Converting an enquiry stamps `deal_id` onto it, so the enquiry caches go too. */
    createBrokerDeal: builder.mutation<IdResult, { enquiryId: number }>({
      query: ({ enquiryId }) => ({ url: 'broker/deals', method: 'POST', body: { enquiryId } }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Deal', id: 'LIST' },
        { type: 'Deal', id: 'DASHBOARD' },
        { type: 'Enquiry', id: 'LIST' },
        { type: 'Enquiry', id: String(arg.enquiryId) },
        { type: 'DashboardStats', id: 'BROKER' }
      ]
    }),

    getBrokerDeal: builder.query<BrokerDealDetail, { id: number }>({
      query: ({ id }) => ({ url: `broker/deals/${id}` }),
      transformResponse: (response: unknown) => normalizeBrokerDeal(response),
      providesTags: (_result, _error, arg) => [{ type: 'Deal', id: String(arg.id) }]
    }),

    updateBrokerDealStage: builder.mutation<MessageResult, { id: number; stage: string }>({
      query: ({ id, stage }) => ({
        url: `broker/deals/${id}/stage`,
        method: 'PUT',
        body: { stage }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Deal', id: 'LIST' },
        { type: 'Deal', id: 'DASHBOARD' },
        { type: 'Deal', id: String(arg.id) }
      ]
    }),

    closeBrokerDeal: builder.mutation<
      MessageResult,
      { id: number; outcome: 'won' | 'lost'; reason?: string }
    >({
      /** `reason` is sent even when undefined, exactly as the axios call did. */
      query: ({ id, outcome, reason }) => ({
        url: `broker/deals/${id}/close`,
        method: 'PUT',
        body: { outcome, reason }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Deal', id: 'LIST' },
        { type: 'Deal', id: 'DASHBOARD' },
        { type: 'Deal', id: String(arg.id) },
        { type: 'DashboardStats', id: 'BROKER' },
        { type: 'Profile', id: 'ME' }
      ]
    }),

    getBrokerDealNotes: builder.query<DealNoteRow[], { id: number }>({
      query: ({ id }) => ({ url: `broker/deals/${id}/notes` }),
      transformResponse: (response: unknown) => normalizeRows<DealNoteRow>(response),
      providesTags: (_result, _error, arg) => [{ type: 'DealNote', id: String(arg.id) }]
    }),

    createBrokerDealNote: builder.mutation<IdResult, { id: number; note: string }>({
      query: ({ id, note }) => ({
        url: `broker/deals/${id}/notes`,
        method: 'POST',
        body: { note }
      }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: (_result, _error, arg) => [{ type: 'DealNote', id: String(arg.id) }]
    }),

    /**
     * FormData body with NO `Content-Type` header: the browser must set it so the multipart
     * boundary is generated. Setting it by hand produces a boundary-less header and multer
     * rejects the whole request.
     */
    uploadBrokerDealDocument: builder.mutation<
      UploadDealDocumentResult,
      { id: number; file: File; documentType: string }
    >({
      query: ({ id, file, documentType }) => {
        const body = new FormData();
        body.append('file', file);
        body.append('documentType', documentType);
        return { url: `broker/deals/${id}/documents`, method: 'POST', body };
      },
      transformResponse: (response: unknown) => normalizeUploadedDealDocument(response),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Deal', id: String(arg.id) }]
    }),

    /* ───────────── profile ───────────── */

    getBrokerProfile: builder.query<BrokerProfileResult, void>({
      query: () => ({ url: 'broker/profile' }),
      transformResponse: (response: unknown) => normalizeBrokerProfile(response),
      providesTags: [{ type: 'Profile', id: 'ME' }]
    }),

    updateBrokerProfile: builder.mutation<MessageResult, UpdateBrokerProfileArgs>({
      query: (body) => ({ url: 'broker/profile', method: 'PUT', body }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: [{ type: 'Profile', id: 'ME' }]
    }),

    /* ───────────── notifications ───────────── */

    getBrokerNotifications: builder.query<BrokerNotification[], void>({
      query: () => ({ url: 'broker/notifications' }),
      transformResponse: (response: unknown) => normalizeRows<BrokerNotification>(response),
      providesTags: [{ type: 'Notification', id: 'LIST' }]
    }),

    markBrokerNotificationRead: builder.mutation<MessageResult, { id: number }>({
      query: ({ id }) => ({ url: `user/notifications/${id}/read`, method: 'PUT' }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: [
        { type: 'Notification', id: 'LIST' },
        { type: 'Notification', id: 'MINI' },
        { type: 'Notification', id: 'ACTIVITY' }
      ]
    })
  })
});

export const {
  useGetBrokerDashboardStatsQuery,
  useGetBrokerDashboardActivityQuery,
  useGetBrokerDashboardAlertsQuery,
  useGetBrokerEnquiryBreakdownQuery,
  useGetBrokerDashboardActiveDealsQuery,
  useGetBrokerDashboardSavedQuickQuery,
  useGetBrokerDashboardNotificationsQuery,
  useGetBrokerMarketSnapshotQuery,
  useGetBrokerPropertiesQuery,
  useGetBrokerPropertyQuery,
  useSaveBrokerPropertyMutation,
  useUnsaveBrokerPropertyMutation,
  useGetSavedPropertiesQuery,
  useGetBrokerEnquiriesQuery,
  useGetBrokerEnquiryQuery,
  useCreateBrokerEnquiryMutation,
  useDeleteBrokerEnquiryMutation,
  useRespondBrokerEnquiryMutation,
  useGetBrokerDealsQuery,
  useCreateBrokerDealMutation,
  useGetBrokerDealQuery,
  useUpdateBrokerDealStageMutation,
  useCloseBrokerDealMutation,
  useGetBrokerDealNotesQuery,
  useCreateBrokerDealNoteMutation,
  useUploadBrokerDealDocumentMutation,
  useGetBrokerProfileQuery,
  useUpdateBrokerProfileMutation,
  useGetBrokerNotificationsQuery,
  useMarkBrokerNotificationReadMutation
} = brokerApi;
